import React, { useCallback, useEffect, useRef, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faCode,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

import { API } from "config";
import CopyButton from "../Message/CopyButton";
import styles from "./Markdown.module.scss";

type VegaTheme = "dark" | "default";

function getAppTheme(): VegaTheme {
  const root = document.documentElement;
  if (root.classList.contains("theme--dark")) return "dark";
  if (root.classList.contains("theme--light")) return "default";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "default";
}

interface FileVariant {
  variant_id: string;
  variant_type: string;
}

// Reject full-Vega-only top-level keys. Vega-Lite uses `mark` (singular).
function isVegaLiteSpec(parsed: unknown): parsed is Record<string, unknown> {
  if (typeof parsed !== "object" || parsed === null) return false;
  const obj = parsed as Record<string, unknown>;
  if (Array.isArray(obj.signals)) return false;
  if (Array.isArray(obj.marks)) return false;
  if (Array.isArray(obj.scales)) return false;
  return true;
}

async function loadVegaModules() {
  const [vega, vegaEmbed, vegaInterpreter] = await Promise.all([
    import("vega"),
    import("vega-embed"),
    import("vega-interpreter"),
  ]);
  return {
    vegaLoader: vega.loader,
    vegaEmbed: vegaEmbed.default,
    expressionInterpreter: vegaInterpreter.expressionInterpreter,
  };
}

// vega-datasets on jsDelivr — see ADR 040. Version segment required, filename
// segment forbids slashes/path-traversal beyond what jsDelivr serves.
const PUBLIC_DATASET_ALLOWLIST =
  /^https:\/\/cdn\.jsdelivr\.net\/npm\/vega-datasets@[^/]+\/data\/[A-Za-z0-9._-]+$/;

function makeFileLoader(
  vegaLoader: typeof import("vega").loader,
  getAccessToken: () => Promise<string>,
  fileApiBaseUrl: string
) {
  const loader = vegaLoader();
  const defaultLoad = loader.load.bind(loader);
  // Override `load` directly: vega-loader's `sanitize` flags any `file://`
  // URI as a local-filesystem read and dispatches to `loader.file`, bypassing
  // `loader.http`. We treat `file://<id>` as a virtual identifier resolved
  // through the file API instead.
  loader.load = async (uri: string, options?: unknown) => {
    if (PUBLIC_DATASET_ALLOWLIST.test(uri)) {
      return defaultLoad(uri, options as Parameters<typeof defaultLoad>[1]);
    }
    if (!uri.startsWith("file://")) {
      throw new Error(
        "Only file:// or allowlisted public-dataset URLs are supported in chart specs"
      );
    }
    const id = uri.slice("file://".length);
    const accessToken = await getAccessToken();
    const authHeader = { Authorization: `Bearer ${accessToken}` };

    // The LLM is instructed to emit `meta.file_id`, but it sometimes emits a
    // `variant_id` instead. Try the file path first; on 404 fall back to
    // resolving the ID as a variant directly.
    let variantId: string;
    const variantsResp = await fetch(
      `${fileApiBaseUrl}/api/v1/files/${id}/variants`,
      { headers: authHeader }
    );
    if (variantsResp.ok) {
      const variants: FileVariant[] = await variantsResp.json();
      const original = variants.find((v) => v.variant_type === "original");
      if (!original) {
        throw new Error(`No "original" variant for file ${id}`);
      }
      variantId = original.variant_id;
    } else if (variantsResp.status === 404) {
      variantId = id;
    } else {
      throw new Error(
        `Failed to list variants for ${id}: ${variantsResp.status}`
      );
    }

    const dataResp = await fetch(
      `${fileApiBaseUrl}/api/v1/variant/${variantId}/permalink`,
      { headers: authHeader }
    );
    if (!dataResp.ok) {
      throw new Error(`Failed to fetch ${id}: ${dataResp.status}`);
    }
    return await dataResp.text();
  };
  return loader;
}

interface VegaLiteChartProps {
  code: string;
  /**
   * When true, the chart stays in source view until streaming ends so
   * vega-embed isn't run against a partial spec.
   */
  isStreaming?: boolean;
  /**
   * Required to fetch chart data from `file://<file_id>` URLs (cc-file-api).
   * When omitted, file:// data references will fail to load.
   */
  getAccessToken?: () => Promise<string>;
}

type RenderResult = {
  finalize: () => void;
  view: { toImageURL: (type: string, scale?: number) => Promise<string> };
};

interface CacheEntry {
  container: HTMLDivElement;
  result: RenderResult;
}

export default React.memo(function VegaLiteChart({
  code,
  isStreaming = false,
  getAccessToken,
}: VegaLiteChartProps) {
  // Once the user manually toggles the view, stop auto-flipping.
  const userInControl = useRef(false);
  // Tracks whether the pending render was explicitly user-requested so that
  // failures are shown as errors rather than suppressed silently.
  const userRequested = useRef(false);
  const [showChart, setShowChart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<VegaTheme>(getAppTheme);

  // Stable container that vega-embed renders into. Mounted once and
  // toggled via display rather than unmounted, so embed runs at most once
  // per (specText, theme) pair.
  const chartHostRef = useRef<HTMLDivElement>(null);
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const activeKeyRef = useRef<string | null>(null);
  const [activeView, setActiveView] = useState<RenderResult["view"] | null>(
    null
  );

  useEffect(() => {
    const update = () => setTheme(getAppTheme());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", update);
    return () => {
      observer.disconnect();
      mq.removeEventListener("change", update);
    };
  }, []);

  // Auto-flip to chart when streaming completes. Prop-drilling avoided —
  // isStreaming is on context. Manual user choice disables auto-flip.
  useEffect(() => {
    if (userInControl.current) return;
    if (!isStreaming) {
      setShowChart(true);
    }
  }, [isStreaming]);

  // Tear down all cached views on unmount.
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      for (const entry of cache.values()) {
        try {
          entry.result.finalize();
        } catch {
          // ignore
        }
        entry.container.remove();
      }
      cache.clear();
    };
  }, []);

  useEffect(() => {
    if (!showChart) return;
    if (!getAccessToken) return;

    const wasUserRequested = userRequested.current;
    userRequested.current = false;

    let cancelled = false;
    const cacheKey = `${theme}:${code}`;

    // Cache hit — re-attach the cached container.
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      const host = chartHostRef.current;
      if (host) {
        // Hide all other cached containers; show the active one.
        for (const [key, entry] of cacheRef.current.entries()) {
          entry.container.style.display = key === cacheKey ? "" : "none";
          if (entry.container.parentNode !== host) {
            host.appendChild(entry.container);
          }
        }
        activeKeyRef.current = cacheKey;
      }
      setActiveView(cached.result.view);
      setError(null);
      return;
    }

    (async () => {
      try {
        const { vegaLoader, vegaEmbed, expressionInterpreter } =
          await loadVegaModules();
        if (cancelled) return;

        const parsed: unknown = JSON.parse(code);
        if (!isVegaLiteSpec(parsed)) {
          throw new Error(
            "Spec is not a valid Vega-Lite spec (full Vega `signals`/`marks`/`scales` arrays are not supported)"
          );
        }

        const host = chartHostRef.current;
        if (!host) return;

        const container = document.createElement("div");
        host.appendChild(container);

        const loader = makeFileLoader(
          vegaLoader,
          getAccessToken,
          API.fileApi.url
        );

        const result = await vegaEmbed(container, parsed, {
          renderer: "svg",
          ast: true,
          expr: expressionInterpreter,
          loader,
          actions: false,
          tooltip: {
            theme: theme === "dark" ? "dark" : "light",
          },
          theme: theme === "dark" ? "dark" : undefined,
        });

        if (cancelled) {
          try {
            result.finalize();
          } catch {
            // ignore
          }
          container.remove();
          return;
        }

        cacheRef.current.set(cacheKey, { container, result });
        // Show this container; hide others.
        for (const [key, entry] of cacheRef.current.entries()) {
          entry.container.style.display = key === cacheKey ? "" : "none";
        }
        activeKeyRef.current = cacheKey;
        setActiveView(result.view);
        setError(null);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (wasUserRequested) {
          setError(message);
        } else {
          setError(null);
          setShowChart(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showChart, code, theme, getAccessToken]);

  const handleRenderClick = useCallback(() => {
    userInControl.current = true;
    userRequested.current = true;
    setError(null);
    setShowChart(true);
  }, []);

  const handleViewSourceClick = useCallback(() => {
    userInControl.current = true;
    setShowChart(false);
  }, []);

  const handleDownloadClick = useCallback(async () => {
    if (!activeView) return;
    try {
      const dataUrl = await activeView.toImageURL("png", 2);
      const link = document.createElement("a");
      link.download = "chart.png";
      link.href = dataUrl;
      link.click();
    } catch {
      // Rare runtime failure during re-render; chart remains on screen.
    }
  }, [activeView]);

  const showSource = !showChart || error !== null;

  return (
    <div className={styles.mermaidContainer}>
      <div className={styles.mermaidHeader}>
        {showSource ? (
          <button className={styles.mermaidToggle} onClick={handleRenderClick}>
            <FontAwesomeIcon icon={faChartColumn} />
            Render Chart
          </button>
        ) : (
          <button
            className={styles.mermaidToggle}
            onClick={handleViewSourceClick}
          >
            <FontAwesomeIcon icon={faCode} />
            View Source
          </button>
        )}
        {showChart && !error && activeView && (
          <button
            className={styles.mermaidToggle}
            style={{ marginLeft: "auto" }}
            onClick={handleDownloadClick}
          >
            <FontAwesomeIcon icon={faDownload} />
            Download
          </button>
        )}
      </div>
      {error && (
        <div className={styles.mermaidError}>
          Vega-Lite render error: {error}
        </div>
      )}
      <div
        ref={chartHostRef}
        className={styles.mermaidDiagram}
        style={{ display: showChart && !error ? "" : "none" }}
      />
      {showSource && (
        <div className={styles.codeBlock}>
          <SyntaxHighlighter
            style={oneDark}
            language="json"
            customStyle={{ borderRadius: "0 0 0.25rem 0.25rem", margin: "0px" }}
            PreTag="div"
          >
            {code}
          </SyntaxHighlighter>
          <CopyButton content={code} />
        </div>
      )}
    </div>
  );
});
